<script setup lang="ts">
  import ItemsService from '@/api/services/items.service';
  import ControlNumberInput from '@/components/control/ControlNumberInput.vue';
  import FormGroup from '@/components/FormGroup.vue';
  import StoreMenuItemAllergens from '@/components/shared/StoreMenuItemAllergens.vue';
  import StoreMenuItemOptions from '@/components/shared/StoreMenuItemOptions.vue';
  import StoreMenuItemPicture from '@/components/shared/StoreMenuItemPicture.vue';
  import StoreMenuItemPrice from '@/components/shared/StoreMenuItemPrice.vue';
  import { useMenuStore } from '@/composition/use-menu';
  import { useModal } from '@/composition/use-modal';
  import { useOrdering } from '@/composition/use-ordering';
  import { useRestaurant } from '@/composition/use-restaurant';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import { ICartItem } from '@/interfaces/ICart';
  import { IItem } from '@/interfaces/IItem';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { roundPrice } from '@/utils';
  import { calculateItemPrice, itemToItemOptions } from '@/utils/orders.utils';
  import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
  import {
    computed,
    provide,
    ref,
    watch,
    nextTick,
    toRef,
    getCurrentInstance,
  } from 'vue';
  import { useRoute } from 'vue-router/composables';
  const breakpoints = useBreakpoints(breakpointsTailwind);

  const ui = useUi();

  const instance = getCurrentInstance();
  const modals = useModal();
  const emits = defineEmits(['close']);

  const props = defineProps<{
    item: IItem;
    editingCartItem?: ICartItem;
  }>();

  const restaurantStore = useRestaurantStore();
  const restaurantHelper = useRestaurant();
  const menuStore = useMenuStore();

  const route = useRoute();

  provide('item', toRef(props, 'item'));

  const pictureSize = computed(() => {
    if (breakpoints.smallerOrEqual('sm').value) return 'md';
    return 'lg';
  });

  const quantity = ref(1);
  const userComment = ref('');

  const selectedOptions = ref<Record<number, Record<number, number>>>({});

  watch(
    () => props.editingCartItem,
    (cartItem) => {
      if (!cartItem) return;

      //console.log("editingCartItem", cartItem);
      quantity.value = cartItem.quantity;
      userComment.value = cartItem.userComment;
      selectedOptions.value = JSON.parse(JSON.stringify(cartItem.optionValues));
    },
    { immediate: true }
  );

  const finalUnitPrice = computed(() => {
    if (!props.item) return 0;

    return calculateItemPrice({
      item: props.item,
      optionValues: selectedOptions.value,
      applyTax: (price) => restaurantHelper.applyTax({ price, partial: true }),
    });
  });

  const totalPrice = computed(() => {
    return roundPrice(finalUnitPrice.value * quantity.value);
  });

  function ensureRequiredOptionsSelected() {
    if (!props.item) return false;

    const itemOptions = itemToItemOptions(props.item).filter(
      (option) =>
        option.active && (option.main || option.mandatory || option.min > 0)
    );

    for (const option of itemOptions) {
      if (!selectedOptions.value[option.id!]) return false;

      const selectedValuesSum = Object.values(
        selectedOptions.value[option.id!]
      ).reduce((acc, value) => acc + value, 0);

      const min = option.min ?? 1;

      if (selectedValuesSum < min) return false;
    }

    return true;
  }

  function addItem() {
    if (!props.item || !quantity.value) return;

    try {
      if (!ensureRequiredOptionsSelected()) {
        ui.showToast(
          AlertType.Error,
          'Seleccione todas las opciones obligatorias'
        );
        return;
      }

      if (!props.editingCartItem) {
        const itemToAdd: ICartItem = {
          item: props.item!,
          quantity: quantity.value,
          unitPrice: finalUnitPrice.value,
          totalPrice: totalPrice.value,
          index: 0,
          itemId: props.item!.id,
          optionValues: selectedOptions.value,
          userComment: userComment.value,
        };

        //emits('closed', itemToAdd);
        instance.proxy.$parent.$emit('save', itemToAdd);
      } else {
        const cartItem = { ...props.editingCartItem };
        cartItem.quantity = quantity.value;
        cartItem.userComment = userComment.value;
        cartItem.unitPrice = finalUnitPrice.value;
        cartItem.totalPrice = totalPrice.value;
        cartItem.optionValues = selectedOptions.value;

        //emits('closed', editingCartItem.value);
        instance.proxy.$parent.$emit('save', cartItem);
      }
      modals.hide('pos-item');

      //ui.showToast(ToastType.SUCCESS, "Item agregado al carrito");
    } catch (error) {
      ui.serverError(error, 'Error agregando el item al carrito');
    }
  }
</script>

<template>
  <div class="modal-container">
    <v-card class="store-item-modal">
      <v-card-title class="tw-flex tw-items-center tw-justify-between">
        <div v-if="item" class="name-container">
          <StoreMenuItemPicture
            :item="item"
            :size="pictureSize"
            placeholder-size="2xl"
          />
          <h3>{{ item.name }}</h3>
        </div>
        <v-skeleton-loader v-else type="text" class="name-placeholder" />
        <div class="spacer"></div>
        <v-btn icon @click="emits('close')" class="close-button" text>
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="tw-flex-1 tw-min-h-1 tw-overflow-y-auto tw-flex">
        <div class="content">
          <div v-if="!item || !!item.picture" class="meta-container">
            <template v-if="!item">
              <v-skeleton-loader type="text" class="description-placeholder" />
            </template>
            <template v-else>
              <StoreMenuItemPrice
                v-if="item.price > 0"
                class="price"
                :price="item.price"
                :discounted-price="item.discountedPrice"
                :has-options="item.hasOptions"
                size="md"
              />
              <p class="description">
                {{ item.description }}
              </p>
              <FormGroup v-if="item.allergens" label="Alérgenos">
                <div class="allergens-container">
                  <StoreMenuItemAllergens :item="item" size="sm" />
                </div>
              </FormGroup>
            </template>
          </div>
          <div class="details-container">
            <template v-if="!item">
              <v-skeleton-loader type="text" class="description-placeholder" />
            </template>
            <template v-else>
              <StoreMenuItemOptions
                v-model="selectedOptions"
                :only-display="!menuStore.canOrder"
              />

              <div v-if="menuStore.canOrder" class="user-comment">
                <div class="header">
                  <span class="header--title">Indicación</span>
                  <span class="optional">Opcional</span>
                </div>
                <v-textarea
                  v-model="userComment"
                  class="input"
                  placeholder="Si el cliente tiene alguna preferencia sobre el producto, escríbala aquí"
                  rows="3"
                  outlined
                />
              </div>
            </template>
          </div>
        </div>
      </v-card-text>

      <v-card-actions v-if="menuStore.canOrder" class="footer">
        <ControlNumberInput v-model="quantity" size="xl" :min="1" />
        <v-btn
          @click="addItem"
          :disabled="quantity === 0"
          large
          class="tw-ml-2"
          depressed
          rounded
          color="green"
          dark
        >
          <v-icon left>mdi-cart-plus</v-icon>
          {{ restaurantStore.currency?.symbol }}{{ totalPrice }} -
          {{ !editingCartItem ? 'Agregar al carrito' : 'Guardar cambios' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<style scoped lang="scss">
  .store-item-modal {
    @apply tw-flex tw-flex-col tw-w-full tw-h-full;

    .name-container {
      @apply tw-flex tw-items-center tw-space-x-4;
    }

    .content {
      @apply tw-flex tw-flex-col tw-w-full;
      @apply sm:tw-space-x-6 sm:tw-items-stretch sm:tw-flex-row;

      .meta-container {
        @apply tw-flex-none sm:tw-w-60 tw-flex tw-flex-col;
      }

      .details-container {
        @apply tw-flex-1 tw-space-y-4 tw-flex tw-flex-col;
        @apply sm:tw-h-full sm:tw-overflow-y-auto sm:tw-px-2 tw-border-dashed sm:tw-border-y tw-pb-4 tw-pt-4;

        .name-placeholder {
          @apply tw-w-32 tw-h-8;
        }

        .description-placeholder {
          @apply tw-w-full tw-h-6;
        }

        .description {
          @apply tw-text-gray-600 tw-font-light tw-break-words tw-whitespace-pre-line tw-text-sm;
        }

        .allergens-container {
        }

        .user-comment {
          @apply tw-space-y-2;

          .header {
            @apply tw-flex tw-items-center tw-space-x-1 tw-px-4;
            @apply tw-text-gray-600;

            &--title {
              @apply tw-font-semibold;
            }

            .optional {
              @apply tw-text-xs;
            }
          }

          .input {
            @apply tw-w-full;
          }
        }
      }
    }

    .footer {
      @apply tw-flex tw-items-center tw-justify-end tw-space-x-2;
    }
  }
</style>
